import { useQueryClient } from '@tanstack/react-query'
import { PrimaryButton, Button, ButtonRow, ErrorNotification } from '@cb/apricot-react'
import { Spinner, isEmpty, formatDate, DATETIME_FORMATS } from '@myap/ui-library'
import {
  useUserDetailsQuery,
  useEducationPeriodsQuery,
  useDeadlinesQuery,
  useStudentScoreSendQuery,
  useSaveStudentScoreSendMutation,
  useStudentEnrollmentsQuery,
  useStudentProfileAndRegistrationQuery,
  useAnalyticsStudentScoreSendSelectRecipientEvent,
  useAnalyticsStudentScoreSendSuccessEvent,
} from '../../hooks'
import ReturnToMyAP from '../_common/ReturnToMyAP'
import DisplayDI from '../scoresend/DisplayDI'
import SearchForDI from '../scoresend/SearchForDI'
import NotAvailable from '../scoresend/messages/NotAvailable'
import BeforeAvailable from '../scoresend/messages/BeforeAvailable'
import NoLongerAvailable from '../scoresend/messages/NoLongerAvailable'
import Available from '../scoresend/messages/Available'
import PromptForScoreSendActionModal from '../scoresend/PromptForScoreSendActionModal'
import { openModal } from '../_common/apricot/Modal'
import { STATUS_LOADING, STATUS_SUCCESS, STATUS_ERROR } from '../_common/reactquery'
import {
  USER_PROMPT_MODAL_ACTION,
  NOT_AVAILABLE,
  BEFORE_AVAILABLE,
  AFTER_AVAILABLE,
  AVAILABLE,
} from '../scoresend/constants'

function StudentApp() {
  const [scoreSendAvailability, setScoreSendAvailability] = useState(null)
  const queryClient = useQueryClient()
  // User Details
  const { data: userDetails = {}, isLoading: isLoadingUserDetails } = useUserDetailsQuery()
  const { studentId } = userDetails
  // Education Periods
  const { data: educationPeriodsData = {}, isLoading: isLoadingEducationPeriods } =
    useEducationPeriodsQuery({ id: studentId, myDetailsComplete: !isLoadingUserDetails })
  const { currentEducationPeriod = {} } = educationPeriodsData
  const {
    hasEnrollments = false,
    isRegistered = false,
    code: educationPeriodCd,
  } = currentEducationPeriod
  // Deadlines
  const { data: deadlinesData } = useDeadlinesQuery({
    educationPeriodCd,
    educationPeriodsComplete: !isLoadingEducationPeriods,
  })

  // The DI that is selected in DI Search
  const [diCodeSelection, setDICodeSelection] = useState({})
  // A place to hold previously selected DI in case the user chooses to cancel the edit
  const [backupDICodeSelection, setBackupDICodeSelection] = useState({})
  const [hasSelectedDI, setHasSelectedDI] = useState(false)
  const [isEditing, setIsEditing] = useState(hasSelectedDI)
  const [showIntervention, setShowIntervention] = useState(false)

  // Profile and Registration
  const {
    isInitialLoading: loadingStudentProfileAndRegistrationData,
    error: studentProfileAndRegistrationError,
  } = useStudentProfileAndRegistrationQuery({
    educationPeriodCd,
    studentId,
    skip: !hasEnrollments || !isRegistered,
  })
  // Enrollments
  const {
    data: studentEnrollmentsData,
    isInitialLoading: loadingStudentEnrollmentsData,
    error: studentEnrollmentsError,
  } = useStudentEnrollmentsQuery({ studentId, hasEnrollments })
  // Score Send
  const {
    data: scoreSendData = {},
    isInitialLoading: loadingScoreSendData,
    error: scoreSendDataError,
  } = useStudentScoreSendQuery({
    educationPeriodCd,
    studentId,
    hasEnrollments,
  })
  const { userPromptType } = scoreSendData
  const { mutate: saveScoreSendDI, status: saveScoreSendDIStatus } =
    useSaveStudentScoreSendMutation({
      educationPeriodCd,
      studentId,
      diCode: diCodeSelection.diCode,
      org: diCodeSelection.org,
    })

  useAnalyticsStudentScoreSendSelectRecipientEvent({
    userPromptType,
  })

  useAnalyticsStudentScoreSendSuccessEvent({
    userPromptType,
    loading: saveScoreSendDIStatus === STATUS_LOADING,
    scoreSendSaved: saveScoreSendDIStatus === STATUS_SUCCESS,
  })

  useEffect(() => {
    setHasSelectedDI(!isEmpty(scoreSendData?.diCode) && !isEmpty(scoreSendData?.org))
  }, [scoreSendData?.diCode, scoreSendData?.org])

  useEffect(() => {
    // It's possible for a user to have a diCode, but if the DI no longer exists,
    // the org will be empty. This condition ensures the user is still able to update
    // the diCode in order to select a valid DI.
    if (!scoreSendData?.diCode || isEmpty(scoreSendData?.org)) {
      setIsEditing(true)
    } else {
      setIsEditing(false)
    }
  }, [scoreSendData?.diCode, scoreSendData?.org])

  useEffect(() => {
    // In the case where a DI has been entered, but that DI is no longer active, we
    // want to display a modal to prompt the user to provide a new DI code.
    if (!isEmpty(scoreSendData?.org?.activeInd) && !scoreSendData?.org?.activeInd) {
      setShowIntervention(true)
      setIsEditing(true)
    }
  }, [scoreSendData?.org?.activeInd])

  useEffect(() => {
    if (userPromptType === USER_PROMPT_MODAL_ACTION || showIntervention) {
      openModal(PromptForScoreSendActionModal, {}, queryClient)
    }
  }, [userPromptType])

  useEffect(() => {
    if (!studentEnrollmentsData?.hasRegistrations) {
      setScoreSendAvailability(NOT_AVAILABLE)
    } else if (!deadlinesData?.diSelectionStartDateIsPast) {
      setScoreSendAvailability(BEFORE_AVAILABLE)
    } else if (deadlinesData?.diSelectionEndDateIsPast) {
      setScoreSendAvailability(AFTER_AVAILABLE)
    } else {
      setScoreSendAvailability(AVAILABLE)
    }
  }, [
    studentEnrollmentsData?.hasRegistrations,
    deadlinesData?.diSelectionStartDateIsPast,
    deadlinesData?.diSelectionEndDateIsPast,
  ])

  const handleClickCancel = () => {
    setDICodeSelection(backupDICodeSelection)
    setBackupDICodeSelection({})
    setIsEditing(false)
  }

  const endRangeDate = formatDate(
    deadlinesData?.diSelectionEndDate,
    DATETIME_FORMATS.longMonthDayYear
  )
  const adminYear = currentEducationPeriod?.apAdminYear

  if (scoreSendDataError || studentEnrollmentsError || studentProfileAndRegistrationError) {
    return (
      <div className="container cb-margin-top-48 cb-no-padding" tabIndex="-1">
        <ReturnToMyAP />
        <ErrorNotification title="Error">
          Error retrieving data. Please try again later.
        </ErrorNotification>
      </div>
    )
  }

  if (
    loadingScoreSendData ||
    loadingStudentEnrollmentsData ||
    loadingStudentProfileAndRegistrationData
  ) {
    return <Spinner size="64" />
  }

  return (
    <div className="container cb-margin-top-48 cb-no-padding" tabIndex="-1">
      <ReturnToMyAP />
      {scoreSendAvailability === NOT_AVAILABLE ? (
        <NotAvailable yearRange={currentEducationPeriod?.descr} />
      ) : scoreSendAvailability === BEFORE_AVAILABLE ? (
        <BeforeAvailable />
      ) : scoreSendAvailability === AFTER_AVAILABLE ? (
        <NoLongerAvailable scoreSendData={scoreSendData} />
      ) : (
        <>
          <Available
            firstTime={!hasSelectedDI}
            endRangeDate={endRangeDate}
            userPromptType={userPromptType}
          />
          {isEditing ? (
            <div className="row">
              <div className="col-md-8 col-xs-12">
                {saveScoreSendDIStatus === STATUS_ERROR ? (
                  <ErrorNotification className="cb-margin-bottom-24" title="Error">
                    Error saving data. Please try again later.
                  </ErrorNotification>
                ) : null}
                <div className="cb-margin-top-24 cb-margin-bottom-16">
                  <SearchForDI
                    setDICodeSelection={setDICodeSelection}
                    fieldName="searchSchoolOnPage"
                    suggestionIdPrefix="autosuggest-on-page-option"
                  />
                </div>
                <ButtonRow>
                  {!isEmpty(scoreSendData?.diCode) ? (
                    <Button onClick={handleClickCancel}>Cancel</Button>
                  ) : null}
                  {scoreSendAvailability === AVAILABLE && isEditing ? (
                    <PrimaryButton
                      disabled={
                        isEmpty(diCodeSelection) || saveScoreSendDIStatus === STATUS_LOADING
                      }
                      onClick={saveScoreSendDI}
                    >
                      Save
                    </PrimaryButton>
                  ) : null}
                </ButtonRow>
              </div>
            </div>
          ) : (
            <DisplayDI
              selectedDI={diCodeSelection}
              setIsEditing={setIsEditing}
              setDICodeSelection={setDICodeSelection}
              setBackupDICodeSelection={setBackupDICodeSelection}
            />
          )}
        </>
      )}
      {scoreSendAvailability === AVAILABLE ? (
        <div className="cb-margin-top-24">
          <h3 className="cb-margin-bottom-16 cb-font-weight-regular">Why Send Scores?</h3>
          <p>Colleges and universities may use your AP scores to:</p>
          <ul>
            <li>Find out your interest in their campus and send you information</li>
            <li>Award scholarships</li>
            <li>Grant you college credit</li>
            <li>Let you skip introductory courses</li>
          </ul>
          <h3 className="cb-margin-top-24 cb-margin-bottom-16 cb-font-weight-regular">
            What scores are sent?
          </h3>
          <p>
            Your selected recipient receives all scores and awards that are available at the time
            the selection is processed in early July.
          </p>
          <p>
            You can see a history of all AP scores you&#39;ve sent to colleges, universities, and
            scholarship programs by clicking <strong>View Past Score Sends</strong>. Once your
            selection has been processed, you can view what date your scores were sent to the
            institution and which scores were included. We aren&#39;t able to confirm when your
            recipient received your score(s). Please contact your institution directly to inquire
            about receipt.
          </p>
          <h3 className="cb-margin-top-24 cb-margin-bottom-16 cb-font-weight-regular">
            Important:
          </h3>
          <ul>
            <li>
              You can change your recipient any time by <strong>{endRangeDate}</strong>. Just return
              to this page and click <strong>Edit</strong> under your selection.
            </li>
            <li>Your score report will be sent in July {adminYear} to the recipient you choose.</li>
          </ul>
        </div>
      ) : null}
      <div className="cb-border-top-1 cb-border-color-black1 cb-border-solid cb-margin-top-48 cb-padding-top-24">
        <ButtonRow>
          <Button
            onClick={e => {
              e.preventDefault()
              window.open('https://apscore.collegeboard.org/scores/send/historyAction.action')
            }}
          >
            View Past Score Sends <span className="sr-only">(Opens in a new window)</span>
          </Button>
        </ButtonRow>
      </div>
    </div>
  )
}

export default StudentApp
